// Feature gating by data (brief 15, item 4), frontend side.
//
// One fetch per practice of GET …/queue/coverage, cached for the life of the tab the
// way useCapabilities caches the capability gate, exposing `gates` — {submit,
// corrections, payment_posting}, each {allowed, state, needs, reason}. An entry point
// reads its gate and, when closed, stays visible but disabled with the reason, so a
// biller learns what the portal still needs rather than earning a 422.
//
// Fail OPEN. The server is the authority (enqueue still refuses on its own rules);
// this hook only decides whether to offer a button. While loading, after a failed
// fetch, for a practice with no submit portal, or where the queue feature is off (the
// route is 404), every gate reads allowed — a network error must not switch a working
// practice's Submit button off.

import { useEffect, useState } from "react";
import { fetchCoverage, normalizeGates } from "./api";

const cache = new Map(); // practiceId -> gates
const inFlight = new Map(); // practiceId -> promise
const subscribers = new Set();

const OPEN = normalizeGates({});

function notify() {
  subscribers.forEach((fn) => fn());
}

// Drop the cached gates and, when anything is mounted, fetch fresh ones: a mounted
// button's effect is keyed on practiceId and would otherwise read OPEN until it
// remounted. Same shape as useCapabilities.invalidateCapabilities.
export function invalidatePmsGates(practiceId) {
  cache.delete(practiceId);
  inFlight.delete(practiceId);
  notify();
  if (practiceId && subscribers.size > 0) fetchOnce(practiceId);
}

// For tests, and for the Runbook page after an upload or approval changes coverage.
export function primePmsGates(practiceId, gates) {
  cache.set(practiceId, normalizeGates(gates));
  inFlight.delete(practiceId);
  notify();
}

function fetchOnce(practiceId) {
  if (inFlight.has(practiceId)) return inFlight.get(practiceId);
  const promise = fetchCoverage(practiceId)
    .then((coverage) => {
      cache.set(practiceId, coverage.gates);
      inFlight.delete(practiceId);
      notify();
      return coverage.gates;
    })
    .catch(() => {
      // Open, and remembered as open: a 404 (feature off) or a failed read is not a
      // reason to retry on every mount of every ledger row.
      cache.set(practiceId, OPEN);
      inFlight.delete(practiceId);
      notify();
      return OPEN;
    });
  inFlight.set(practiceId, promise);
  return promise;
}

export default function usePmsGates(practiceId) {
  const [, forceRender] = useState(0);

  useEffect(() => {
    const fn = () => forceRender((n) => n + 1);
    subscribers.add(fn);
    return () => subscribers.delete(fn);
  }, []);

  useEffect(() => {
    if (!practiceId || cache.has(practiceId)) return;
    fetchOnce(practiceId);
  }, [practiceId]);

  const gates = (practiceId && cache.get(practiceId)) || OPEN;
  return { gates, loading: !!practiceId && !cache.has(practiceId) };
}
